import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../../lib/api'

interface ModerationItem {
  id: string
  contentType: 'post' | 'comment' | 'message' | 'bio' | 'group'
  contentId: string
  contentPreview: string
  contentUrl?: string
  authorId: string
  authorUsername: string
  authorAgeGroup: string
  priority: number
  source: 'report' | 'auto_detection' | 'manual'
  reportCount: number
  uniqueReporters: number
  scores: {
    toxicity?: number
    severeToxicity?: number
    identityAttack?: number
    insult?: number
    profanity?: number
    threat?: number
    sexuallyExplicit?: number
    flirtation?: number
  }
  status: 'pending' | 'reviewed' | 'resolved'
  createdAt: string
}

type ModerationAction = 'approve' | 'remove' | 'warn' | 'suspend' | 'ban'

export default function ModerationQueuePage() {
  const [items, setItems] = useState<ModerationItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'high_priority' | 'auto_flagged' | 'reported'>('all')
  const [selectedItem, setSelectedItem] = useState<ModerationItem | null>(null)

  useEffect(() => {
    fetchQueue()
  }, [filter])

  const fetchQueue = async () => {
    setLoading(true)
    try {
      const response = await api.get('/admin/moderation/queue', {
        params: { filter, limit: 50 },
      })
      setItems(response.data.items || [])
    } catch (error) {
      console.error('Failed to fetch moderation queue:', error)
      // Mock data for development
      setItems([
        {
          id: '1',
          contentType: 'post',
          contentId: 'post-123',
          contentPreview: 'This is some potentially offensive content that was flagged...',
          authorId: 'user-456',
          authorUsername: 'suspicious_user',
          authorAgeGroup: 'teen',
          priority: 80,
          source: 'auto_detection',
          reportCount: 0,
          uniqueReporters: 0,
          scores: {
            toxicity: 0.75,
            severeToxicity: 0.45,
            insult: 0.68,
            profanity: 0.82,
          },
          status: 'pending',
          createdAt: '2024-01-15T10:30:00Z',
        },
        {
          id: '2',
          contentType: 'comment',
          contentId: 'comment-789',
          contentPreview: 'A comment that was reported by multiple users for harassment...',
          authorId: 'user-111',
          authorUsername: 'troll_account',
          authorAgeGroup: 'child',
          priority: 90,
          source: 'report',
          reportCount: 5,
          uniqueReporters: 4,
          scores: {
            toxicity: 0.88,
            identityAttack: 0.72,
          },
          status: 'pending',
          createdAt: '2024-01-15T09:45:00Z',
        },
        {
          id: '3',
          contentType: 'message',
          contentId: 'msg-222',
          contentPreview: 'Direct message flagged for potentially inappropriate content...',
          authorId: 'user-333',
          authorUsername: 'new_user_2024',
          authorAgeGroup: 'adult',
          priority: 60,
          source: 'auto_detection',
          reportCount: 0,
          uniqueReporters: 0,
          scores: {
            flirtation: 0.72,
            sexuallyExplicit: 0.35,
          },
          status: 'pending',
          createdAt: '2024-01-15T11:00:00Z',
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (itemId: string, action: ModerationAction, notes?: string) => {
    try {
      await api.post(`/admin/moderation/queue/${itemId}/action`, {
        action,
        notes,
      })
      setItems(items.filter((item) => item.id !== itemId))
      setSelectedItem(null)
    } catch (error) {
      console.error('Failed to perform action:', error)
    }
  }

  const getPriorityColor = (priority: number) => {
    if (priority >= 80) return 'text-red-400 bg-red-500/20'
    if (priority >= 50) return 'text-yellow-400 bg-yellow-500/20'
    return 'text-gray-400 bg-gray-500/20'
  }

  const getContentTypeIcon = (type: ModerationItem['contentType']) => {
    const icons = {
      post: '📝',
      comment: '💬',
      message: '✉️',
      bio: '👤',
      group: '👥',
    }
    return icons[type]
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Link to="/admin" className="text-gray-400 hover:text-white">
                Admin
              </Link>
              <span className="text-gray-600">/</span>
              <h1 className="text-xl font-bold">Moderation Queue</h1>
            </div>
            <p className="text-gray-400 text-sm">{items.length} items pending review</p>
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {(['all', 'high_priority', 'auto_flagged', 'reported'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg transition ${
                filter === f
                  ? 'bg-pink-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {f.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-xl border border-gray-700">
            <span className="text-6xl mb-4 block">✨</span>
            <h3 className="text-xl font-semibold mb-2">Queue is empty!</h3>
            <p className="text-gray-400">All content has been reviewed.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Queue List */}
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className={`bg-gray-800 rounded-xl p-4 border cursor-pointer transition ${
                    selectedItem?.id === item.id
                      ? 'border-pink-500'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span>{getContentTypeIcon(item.contentType)}</span>
                      <span className="capitalize">{item.contentType}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(item.priority)}`}>
                        P{item.priority}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(item.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 line-clamp-2 mb-2">{item.contentPreview}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>@{item.authorUsername} ({item.authorAgeGroup})</span>
                    {item.reportCount > 0 && (
                      <span className="text-red-400">🚩 {item.reportCount} reports</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Detail Panel */}
            {selectedItem && (
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 sticky top-6">
                <h3 className="text-lg font-semibold mb-4">Content Details</h3>

                {/* Content Preview */}
                <div className="bg-gray-900 rounded-lg p-4 mb-4">
                  <p className="text-sm">{selectedItem.contentPreview}</p>
                </div>

                {/* Author Info */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Author</h4>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                      👤
                    </div>
                    <div>
                      <p className="font-medium">@{selectedItem.authorUsername}</p>
                      <p className="text-xs text-gray-500">
                        Age group: {selectedItem.authorAgeGroup}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Toxicity Scores */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-400 mb-2">AI Analysis</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedItem.scores).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-xs w-32 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <div className="flex-1 bg-gray-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              (value || 0) >= 0.7
                                ? 'bg-red-500'
                                : (value || 0) >= 0.5
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${(value || 0) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs w-12 text-right">
                          {((value || 0) * 100).toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Report Info */}
                {selectedItem.reportCount > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Reports</h4>
                    <p className="text-sm">
                      {selectedItem.reportCount} reports from {selectedItem.uniqueReporters} users
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="border-t border-gray-700 pt-4">
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Actions</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleAction(selectedItem.id, 'approve')}
                      className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition"
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => handleAction(selectedItem.id, 'remove')}
                      className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition"
                    >
                      ✕ Remove
                    </button>
                    <button
                      onClick={() => handleAction(selectedItem.id, 'warn')}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-lg transition"
                    >
                      ⚠ Warn User
                    </button>
                    <button
                      onClick={() => handleAction(selectedItem.id, 'suspend')}
                      className="bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg transition"
                    >
                      🚫 Suspend
                    </button>
                  </div>
                  <button
                    onClick={() => handleAction(selectedItem.id, 'ban')}
                    className="w-full mt-2 bg-red-800 hover:bg-red-900 text-white py-2 px-4 rounded-lg transition"
                  >
                    ⛔ Ban User
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
